import React, { memo, useContext, useState } from "react";
import { Box, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";
import { AppContext } from "../../AppContextProvider";
import { updateRfq } from "../../api/ProcurementClient";
import User from "../../models/User";
import EmployeeAutocomplete from "./EmployeeAutocomplete";
import {
  bumpRfqClientEpoch,
  mergeRfqIntoList,
} from "../../utils/mergeRfqIntoList";

interface RfqAssignedToCellProps {
  rfqId: number;
  assignedToId: number | null;
  /** Display label from the row — avoids looking up users until edit. */
  assignedToLabel: string;
}

const RfqAssignedToCell: React.FC<RfqAssignedToCellProps> = ({
  rfqId,
  assignedToId,
  assignedToLabel,
}) => {
  const { users = [], setRfqs, setSnackbarErrorMsg } = useContext(AppContext);
  const [editing, setEditing] = useState(false);

  const value =
    assignedToId != null
      ? (users.find((u) => u.id === assignedToId) ?? null)
      : null;

  const handleAssignmentChange = async (selected: User | null) => {
    const nextId = selected?.id ?? null;
    if (nextId === assignedToId) {
      setEditing(false);
      return;
    }
    bumpRfqClientEpoch(rfqId);
    setRfqs((prev) =>
      prev.map((q) =>
        q.id === rfqId ? { ...q, assignedToId: nextId } : q
      )
    );
    setEditing(false);
    try {
      const updatedRfq = await updateRfq(rfqId, { assignedToId: nextId });
      bumpRfqClientEpoch(rfqId);
      setRfqs((prev) => mergeRfqIntoList(prev, updatedRfq));
    } catch (error) {
      bumpRfqClientEpoch(rfqId);
      setRfqs((prev) =>
        prev.map((q) =>
          q.id === rfqId ? { ...q, assignedToId } : q
        )
      );
      setSnackbarErrorMsg?.(
        error instanceof Error ? error.message : "Failed to update assignment"
      );
    }
  };

  return (
    <Box
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      sx={{
        display: "flex",
        alignItems: "center",
        height: "100%",
        width: "100%",
        minWidth: 0,
      }}
    >
      {editing ? (
        <EmployeeAutocomplete
          users={users}
          value={value}
          hideCaption
          placeholder="Unassigned"
          onChange={handleAssignmentChange}
          sx={{ width: "100%" }}
          autoFocus
          onBlur={() => setEditing(false)}
        />
      ) : (
        <Typography
          component="button"
          type="button"
          onClick={() => setEditing(true)}
          sx={{
            border: "none",
            background: "none",
            padding: 0,
            margin: 0,
            font: "inherit",
            cursor: "pointer",
            textAlign: "left",
            width: "100%",
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            color: assignedToLabel ? "text.primary" : grey[400],
            fontStyle: assignedToLabel ? "normal" : "italic",
          }}
        >
          {assignedToLabel || "Unassigned"}
        </Typography>
      )}
    </Box>
  );
};

export default memo(RfqAssignedToCell);
